import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Card, TableRow } from '@mui/material';
import TableCell from 'src/components/Table/TableCell';
import { useTranslation } from 'react-i18next';
import Table from 'src/components/Table';
import { useSelector } from 'react-redux';
import { isLoadingSelector } from 'src/store/selectors/statusSelector';
import { LOAD_CLIENT_EVENTS } from 'src/store/actions/clientActions';
import NotFoundRecords from 'src/components/NotFoundRecords';
import selectFormatDate from 'src/utils/selectDateFormat';

const formatChanges = (changes, t) =>
  (changes || [])
    .map(change => {
      if (change.field === 'legalStatus') {
        const oldValueLabel = t(
          `CLIENTS_VIEW.CLIENT_DETAIL.LEGAL_STATUS_${change.oldValue}`
        );
        const newValueLabel = t(
          `CLIENTS_VIEW.CLIENT_DETAIL.LEGAL_STATUS_${change.newValue}`
        );
        return `${oldValueLabel} → ${newValueLabel}`;
      }
      return `${change.field}: ${change.oldValue} → ${change.newValue}`;
    })
    .join(', ');

const Tbody = ({ clientEvents, t }) =>
  clientEvents.map(event => (
    <TableRow key={event.id} hover>
      <TableCell align="center">
        {selectFormatDate(event.createdAt)}
      </TableCell>
      <TableCell align="center">{formatChanges(event.changes, t)}</TableCell>
      <TableCell align="center">{event.description}</TableCell>
      <TableCell align="center">{event.user?.name || t('COMMON.NO_DATA')}</TableCell>
    </TableRow>
  ));

const ClientEventsTable = ({ className, clientEvents, ...rest }) => {
  const { t } = useTranslation();
  const loading = useSelector(isLoadingSelector([LOAD_CLIENT_EVENTS.action]));

  const theads = useMemo(
    () => () => (
      <TableRow>
        <TableCell variant="head" align="center">
          {t('CLIENTS_VIEW.CLIENT_DETAIL.CLIENT_EVENTS_DATE')}
        </TableCell>
        <TableCell variant="head" align="center">
          {t('CLIENTS_VIEW.CLIENT_DETAIL.CLIENT_EVENTS_CHANGE')}
        </TableCell>
        <TableCell variant="head" align="center">
          {t('CLIENTS_VIEW.CLIENT_DETAIL.CLIENT_EVENTS_DESCRIPTION')}
        </TableCell>
        <TableCell variant="head" align="center">
          {t('CLIENTS_VIEW.CLIENT_DETAIL.CLIENT_EVENTS_USER')}
        </TableCell>
      </TableRow>
    ),
    [t]
  );

  const tbody = useMemo(
    () => () => <Tbody clientEvents={clientEvents} t={t} />,
    [clientEvents, t]
  );

  if (!loading && !clientEvents.length) {
    return (
      <Card>
        <NotFoundRecords
          title="NOT_FOUND_RECORDS_MESSAGE.CLIENT_EVENTS_TITLE"
          description="NOT_FOUND_RECORDS_MESSAGE.CLIENT_EVENTS_DESCRIPTION"
        />
      </Card>
    );
  }

  return (
    <Table {...rest} variant="outlined" tbody={tbody} theads={theads} loading={loading} />
  );
};

ClientEventsTable.propTypes = {
  className: PropTypes.string,
  clientEvents: PropTypes.array.isRequired
};

export default ClientEventsTable;
